import { ContentLayout } from "@pages/content-layout";
import { MemberProfileHeader } from "@pages/member-profile/member-profile-header";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { MemberInfo } from "@pages/member-profile/tab-contents/member-info-tab/member-info";
import { LoadingScreen } from "@components/loading-screen";
import { useAppParams } from "@router/router-helper";
import { MemberProfileTab } from "@router/route-params";
import { PageNotFound } from "@pages/page-not-found";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { memberStateSelector } from "@store/member/member-slice";
import { useEffect } from "react";
import { fetchMember } from "@store/member/member-thunks";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { MemberProjects } from "@pages/member-profile/tab-contents/member-project-tab/member-projects";
import { MemberWorkspaces } from "@pages/member-profile/tab-contents/member-workspace-tab/member-workspaces";

/** Fetch member data and display the data in member profile page */
export function MemberProfilePage(): React.JSX.Element {
  const { memberId, memberProfileTab } = useAppParams();
  const { fetchingMember, member } = useAppSelector(memberStateSelector);
  const dispatch = useAppDispatch();

  const { handleErrorWithPage } = useErrorHandler();

  // Fetches the member
  useEffect(() => {
    async function fetchMemberData(): Promise<void> {
      try {
        await dispatch(fetchMember({ memberId })).unwrap();
      } catch (error) {
        handleErrorWithPage({
          id: `MemberProfilePage-${Date.now().toString()}`,
          title: "Could not load member",
          error,
        });
      }
    }

    void fetchMemberData();
  }, [dispatch, memberId, handleErrorWithPage]);

  if (
    !memberProfileTab ||
    !Object.values(MemberProfileTab).includes(memberProfileTab)
  ) {
    return <PageNotFound />;
  }

  return (
    <LoadingScreen
      isFetching={fetchingMember.selectedMember === "pending"}
      data={member}
    >
      {member && (
        <ContentLayout
          header={<MemberProfileHeader member={member} />}
          tabs={[
            {
              label: "Information",
              route: MemberProfileTab.memberInformation,
              content: <MemberInfo member={member} />,
            },
            {
              label: "Workspaces",
              route: MemberProfileTab.companies,
              content: <MemberWorkspaces companies={member.companies} />,
            },
            {
              label: "Projects",
              route: MemberProfileTab.projects,
              content: <MemberProjects memberId={member.identity} />,
            },
          ]}
          selectedTab={memberProfileTab}
          trackingEventPageName={OpenTabEvents.openMemberProfileTabs}
        />
      )}
    </LoadingScreen>
  );
}
